import { i18n, LoggerService } from '@umanforge/common';
import { TypeOptions, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/**
 * Service de gestion des notifications (toasts).
 * Permet d'afficher des messages contextuels avec un système de log intégré.
 */
class ToastService {
  private context: string;

  constructor( context: string = 'ToastProvider' ) {
    this.context = context;
  }

  /**
   * Définit un nouveau contexte pour le logger des toasts.
   * @param context - Le nouveau contexte (ex: 'settings.service')
   * @returns {ToastService} - Retourne une nouvelle instance avec ce contexte.
   */
  static context( context: string ): ToastService {
    return new ToastService( context );
  }

  /**
   * Affiche un toast avec un message localisé et un type spécifié
   * @param toastType - Le type du toast ('success', 'info', 'warning', 'error')
   * @param messageKey - La clé du message à afficher ou le mssage déjà traduit
   * @param params - Les paramètres pour la localisation du message
   * @returns {string} - Le message affiché dans le toast
   */
  private showToast( toastType: TypeOptions, messageKey: string, params?: Record<string, unknown> ): string {
    let message: string = messageKey;
    if ( i18n.exists( messageKey ) ) {
      message = i18n.t( messageKey, params );
    } else if ( params ) {
      Object.entries( params ).forEach( ( [ key, value ] ) => {
        message = message.replace( new RegExp( `{${key}}`, 'g' ), String( value ) );
      } );
    }

    toast( message, { type: toastType } );
    return message;
  }

  showSuccess( messageKey: string, params?: Record<string, unknown> ) {
    const msg = this.showToast( 'success', messageKey, params );
    new LoggerService( this.context ).verbose( msg );
  }

  showInfo( messageKey: string, params?: Record<string, unknown> ) {
    const msg = this.showToast( 'info', messageKey, params );
    new LoggerService( this.context ).verbose( msg );
  }

  showWarning( messageKey: string, params?: Record<string, unknown> ) {
    const msg = this.showToast( 'warning', messageKey, params );
    new LoggerService( this.context ).warn( msg );
  }

  showError( messageKey: string, params?: Record<string, unknown> ) {
    const msg = this.showToast( 'error', messageKey, params );
    new LoggerService( this.context ).error( msg );
  }
}

export { ToastService };